import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, WritableSignal, computed, effect, inject, signal, } from '@angular/core';
import { HeaderService } from '../header.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FilterType, FilterTypes } from './search-bar.types';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ResolutionService } from '../../resolution.service';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { ProductsService } from '../../../product/products.service';
import { FilterParams } from '../../../product/product.types';
import { ActivatedRoute, Router } from '@angular/router';
import { SignalsStoreService } from '../../signals-store.service';
import { MatInputModule } from '@angular/material/input';
import { toObservable } from '@angular/core/rxjs-interop';
import { SidebarService } from '../../sidebar/sidebar.service';
import { TypesenseSearchService, SearchSuggestion } from '../../typesense-search.service';
import { EMPTY, Subject, debounceTime, distinctUntilChanged, finalize, from, switchMap, take, takeUntil, tap } from 'rxjs';
import { AutocompleteProductCardComponent } from './autocomplete-product-card/autocomplete-product-card.component';
import { NotificationService } from '../../notification/notification.service';
import { OrderService } from '../../order.service';
import { LocalStorageService } from '../../local-storage.service';
import { ModalContentService } from '../../modal-content/modal-content.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isAbandonedUser, isAddressUpdateRequired, isNoAddressUser, isANoPaymentMethodUser, trackAddToCartEvent, arrayToMapMultiKey, getShopScrollElement, getCurrentPathFromUrl, findTopVisibleProductId } from '../../common/utils';
import { updateFirebaseOrderAndSubTrigger } from '../../common/order.utils';
import { UpdateOrderParams } from '../../types/order.type';
import { ProductType } from '../../types/common.enums';
import { ProductCardV2Service } from '../../product-card-v2/product-card-v2.service';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { Session } from '../../types/session.type';
import { getProductCutOffNotice, hasCutOffRuleForDeliveryDay, isProductCutOffLocked, PRODUCT_CUTOFF_LOCK_LABEL } from '../../common/product-cutoff.util';
import { FirebaseCrudService } from '../../firebase-crud.service';
import { DataLayerService } from '../../data-layer/data-layer.service';
import { KlaviyoService } from '../../klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from '../../klaviyo/events';

@Component({
  selector: 'app-search-bar',
  imports: [
    FormsModule,
    CommonModule,
    MatChipsModule,
    AutocompleteProductCardComponent
  ],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss'
})
export class SearchBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;

  private headerService = inject(HeaderService);
  private productService = inject(ProductsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private signalsStoreService = inject(SignalsStoreService);
  private typesenseService = inject(TypesenseSearchService);
  private notificationService = inject(NotificationService);
  private orderService = inject(OrderService);
  private localStorageService = inject(LocalStorageService);
  private modalContentService = inject(ModalContentService);
  private activeModal = inject(NgbModal);
  private productCardV2Service = inject(ProductCardV2Service);
  private firebaseCrudService = inject(FirebaseCrudService);
  private dataLayerService = inject(DataLayerService);
  private klaviyoService = inject(KlaviyoService);
  private resolutionService = inject(ResolutionService);
  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();

  searchBarContent: WritableSignal<string> =
    this.headerService.searchBarContent;

  filters: WritableSignal<Map<string, { type: FilterTypes; value: string }>> = signal(new Map());

  filterTypes: Map<FilterTypes, WritableSignal<number>> = new Map();

  selectedIndex = -1;

  // Typesense autocomplete
  suggestions = this.typesenseService.suggestions;
  popularProducts = this.typesenseService.popularProducts;
  popularSearches = this.typesenseService.popularSearches;
  recentSearches = this.typesenseService.recentSearches;
  querySuggestions = this.typesenseService.querySuggestions;
  addingToCartProductId: WritableSignal<string | null> = signal(null);
  isLoadingSuggestions: WritableSignal<boolean> = signal(false);
  showProductSuggestions: WritableSignal<boolean> = signal(true);
  showDropdown: WritableSignal<boolean> = signal(false);
  isLoadingPopular: WritableSignal<boolean> = signal(false);
  private searchInput$ = new Subject<string>();
  private popularProductsLoaded = false;

  getFilterChipRemovedKey$ = toObservable(this.signalsStoreService.getFilterChipRemoved);

  isInShopComponent = computed(() => this.signalsStoreService.getIsInShopComponent());
  hasSession = this.signalsStoreService.hasSession;

  isQuerying = computed(() => {
    const q = this.searchBarContent();
    return !!q && q.length >= 2;
  });

  suggestionsTotalFound = this.typesenseService.suggestionsTotalFound;
  private suggestionsReturnedCount = this.typesenseService.suggestionsReturnedCount;

  /**
   * Trailing "Search all N products" tile in the grid. Only worth showing when the query
   * actually matched more documents than the dropdown had room for — with everything already
   * on screen the tile would just repeat the left column's "Search all" row. Both sides of
   * the comparison are document counts, never `suggestions().length`, which counts expanded
   * variant cards and would read as "more results" for a single multi-package product.
   *
   * Gated on showProductSuggestions() as well: the tile lives inside the products grid, which
   * this client hides entirely when that flag is off. Without it the flat nav index would
   * reserve a slot for a tile that never renders.
   */
  showSearchAllCard = computed(() =>
    this.isQuerying()
    && this.showProductSuggestions()
    && this.suggestions().length > 0
    && this.suggestionsTotalFound() > this.suggestionsReturnedCount()
  );

  // Unified left-column + right-column model so keyboard nav has a single flat index.
  // leftItems first (recents OR popular when idle; query suggestions + 'search-all' when querying),
  // then products on the right, then the trailing search-all tile when it is shown.
  flatNavItems = computed<{
    leftItems: { kind: 'query' | 'recent' | 'popular' | 'search-all'; value: string }[];
    products: SearchSuggestion[];
    hasSearchAllCard: boolean;
    total: number;
  }>(() => {
    const leftItems: { kind: 'query' | 'recent' | 'popular' | 'search-all'; value: string }[] = [];
    const query = this.searchBarContent();
    const querying = !!query && query.length >= 2;

    if (querying) {
      for (const s of this.querySuggestions()) leftItems.push({ kind: 'query', value: s });
      leftItems.push({ kind: 'search-all', value: query });
    } else if (this.recentSearches().length > 0) {
      for (const s of this.recentSearches()) leftItems.push({ kind: 'recent', value: s });
    } else {
      for (const s of this.popularSearches()) leftItems.push({ kind: 'popular', value: s });
    }

    const products = this.showProductSuggestions()
      ? (querying ? this.suggestions() : this.popularProducts())
      : [];

    const hasSearchAllCard = this.showSearchAllCard();
    return {
      leftItems,
      products,
      hasSearchAllCard,
      total: leftItems.length + products.length + (hasSearchAllCard ? 1 : 0),
    };
  });

  totalSuggestionItems = computed(() => this.flatNavItems().total);

  // Tracks the previous open-state of the search bar so we can capture the scroll position
  // exactly on the false → true transition.
  private prevSearchBarOpen = false;

  constructor() {
    // Lock body scroll while the mobile overlay is open. iOS Safari especially needs this:
    // background scroll-chains under fixed overlays without it.
    // Also capture the shop scroll position the moment search opens, so it can be restored
    // when the user clears the search and returns to the same path (Scenario A).
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const open = this.headerService.displaySearchBar();
      const isMobile = this.resolutionService.isMobile();
      document.body.classList.toggle('search-overlay-open', open && isMobile);

      const openingNow = open && !this.prevSearchBarOpen;
      this.prevSearchBarOpen = open;
      if (openingNow) {
        const scroller = getShopScrollElement();
        const scrollTop = scroller?.scrollTop ?? 0;
        const path = getCurrentPathFromUrl(this.router.url);
        // Anchor by the top-visible product id when possible; the numeric scrollTop is
        // kept as a fallback for cases where the product can't be located after the clear.
        const topProductId = findTopVisibleProductId(scroller, scrollTop);
        console.log(path);
        console.log(topProductId);
        this.signalsStoreService.preSearchScroll.set({
          path,
          scrollTop,
          topProductId,
        });
      }
    });
  }

  ngOnInit(): void {
    this.setUpFilterTypesMap();
    this.setupTypesenseAutocomplete();
    this.typesenseService.loadRecentSearches();

    // Hydrate from ?q= so the input keeps the query across navigations/refreshes.
    const initialQuery = this.router.parseUrl(this.router.url).queryParams['q'];
    if (initialQuery) this.searchBarContent.set(initialQuery);

    this.getFilterChipRemovedKey$.subscribe((key) => {
      if (!key) return;
      this.remove(key);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('search-overlay-open');
    }
  }

  private setupTypesenseAutocomplete(): void {
    this.searchInput$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        // switchMap cancels stale in-flight requests so fast typers never see flicker.
        switchMap((query) => {
          if (query.length < 2) {
            this.typesenseService.clearSuggestions();
            this.isLoadingSuggestions.set(false);
            return EMPTY;
          }
          this.isLoadingSuggestions.set(true);
          return from(this.typesenseService.getAutocompleteSuggestions(query, 6))
            .pipe(
              tap(() => this.#resolveCutOff(this.typesenseService.suggestions)),
              finalize(() => this.isLoadingSuggestions.set(false))
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    if (this.signalsStoreService.filterByProducer()) {
      this.searchBarContent.set(this.signalsStoreService.filterByProducer());
      this.add(FilterTypes.producers);
    }

    if (this.searchInput && this.searchInput.nativeElement) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      });
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    const total = this.totalSuggestionItems();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (total === 0) return;
      this.selectedIndex = this.selectedIndex < 0 ? 0 : (this.selectedIndex + 1) % total;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (total === 0) return;
      this.selectedIndex = this.selectedIndex <= 0 ? total - 1 : this.selectedIndex - 1;
    } else if (event.key === 'Home') {
      if (this.selectedIndex < 0 || total === 0) return;
      event.preventDefault();
      this.selectedIndex = 0;
    } else if (event.key === 'End') {
      if (this.selectedIndex < 0 || total === 0) return;
      event.preventDefault();
      this.selectedIndex = total - 1;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const query = this.searchBarContent();

      if (this.selectedIndex >= 0 && this.selectedIndex < total) {
        this.activateSelection();
      } else if (query && query.length >= 2) {
        // No row highlighted — default action is "search all" for the typed query.
        this.navigateToSearch(query);
      }
    } else if (event.key === 'Escape') {
      this.showDropdown.set(false);
      this.selectedIndex = -1;
      this.searchInput?.nativeElement?.blur();
    }
  }

  private activateSelection(): void {
    const { leftItems, products } = this.flatNavItems();
    const i = this.selectedIndex;
    if (i < 0) return;

    if (i < leftItems.length) {
      const item = leftItems[i];
      this.navigateToSearch(item.value);
      return;
    }

    const product = products[i - leftItems.length];
    if (product) {
      this.onProductCardClick(product);
      return;
    }

    // Past the last card: the trailing search-all tile, which repeats the left column's action.
    const query = (this.searchBarContent() || '').trim();
    if (i === leftItems.length + products.length && query.length >= 2) {
      this.navigateToSearch(query);
    }
  }

  onItemHover(index: number): void {
    this.selectedIndex = index;
  }

  onLeftItemRemove(event: Event, value: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.typesenseService.removeRecentSearch(value);
  }

  onClearRecent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.typesenseService.clearRecentSearches();
  }

  onSuggestionSelected(suggestion: SearchSuggestion): void {
    this.typesenseService.clearSuggestions();
    this.searchBarContent.set('');
    this.showDropdown.set(false);

    // Navigate to product detail page
    if (suggestion.productUrl) {
      this.router.navigateByUrl(`/shop/${suggestion.productUrl}`);
    }
  }

  navigateToSearch(query: string): void {
    const trimmed = (query || '').trim();
    if (!trimmed) return;

    // Persist for next session and surface in the "Recent searches" section.
    this.typesenseService.addRecentSearch(trimmed);

    this.typesenseService.clearSuggestions();
    // Reset the debounced pipeline's distinctUntilChanged memory so retyping the same query
    // later (after clearing the input) is treated as a fresh emit and refetches suggestions.
    this.searchInput$.next('');
    this.showDropdown.set(false);
    this.selectedIndex = -1;
    this.searchInput?.nativeElement?.blur();

    // Keep the query visible in the input so users can refine on the results page.
    this.searchBarContent.set(trimmed);

    this.signalsStoreService.isInSearchMode.set(true);
    this.signalsStoreService.searchModeQuery.set(trimmed);

    // If already in shop, don't navigate — ShopComponent reacts to signal changes
    if (!this.isInShopComponent()) {
      this.router.navigate(['/shop'], { queryParams: { q: trimmed } });
    }

    this.closeMobileOverlay();
  }

  /**
   * Dismiss the mobile full-page overlay once the user has navigated to a search
   * result or product page. On desktop the inline search bar stays mounted; only
   * the mobile overlay needs to step out of the way to reveal the results below.
   */
  private closeMobileOverlay(): void {
    if (this.resolutionService.isMobile() && this.headerService.displaySearchBar()) {
      this.headerService.displaySearchBar.set(false);
    }
  }

  onQuerySuggestionClick(suggestion: string): void {
    this.navigateToSearch(suggestion);
  }

  async onSearchFocus(): Promise<void> {
    this.showDropdown.set(true);

    const query = this.searchBarContent();

    // Coming back to the input on the results page: input still has the query (we preserve it
    // through navigation) but navigateToSearch cleared the suggestions cache. Re-fetch directly,
    // bypassing the debounced pipeline whose distinctUntilChanged would drop a same-value emit.
    if (query && query.length >= 2 && this.suggestions().length === 0 && !this.isLoadingSuggestions()) {
      this.isLoadingSuggestions.set(true);
      try {
        await this.typesenseService.getAutocompleteSuggestions(query, 6);
        this.#resolveCutOff(this.typesenseService.suggestions);
      } finally {
        this.isLoadingSuggestions.set(false);
      }
      return;
    }

    // Load popular products and searches if not already loaded and the input is empty.
    if (!this.popularProductsLoaded && !query) {
      this.isLoadingPopular.set(true);
      await Promise.all([
        this.typesenseService.getPopularProducts(8),
        this.typesenseService.getPopularSearches(6)
      ]);
      // The popular grid is the same card, shown on an empty input, so it resolves the same way.
      this.#resolveCutOff(this.typesenseService.popularProducts);
      this.popularProductsLoaded = true;
      this.isLoadingPopular.set(false);
    }
  }

  onSearchBlur(event: FocusEvent): void {
    // On mobile the search-bar lives inside a full-page overlay; the dropdown is the body of
    // the page and must stay visible even when the input loses focus (e.g. user taps to scroll
    // the product row). Cancel is the only way out.
    if (this.resolutionService.isMobile() && this.headerService.displaySearchBar()) return;

    // If focus moved into the dropdown (mousedown.preventDefault on items keeps blur from firing,
    // but tab/keyboard-focus may still land there), keep the dropdown open.
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('.autocomplete-dropdown')) return;
    this.showDropdown.set(false);
    this.selectedIndex = -1;
    // Intentionally NOT calling restorePreSearchScroll on blur. If the user dismissed the
    // bar without running a query, scroll never moved; jumping to the captured top-product
    // would be a perceived bug. preSearchScroll is overwritten on the next bar-open anyway.
  }

  onProductCardClick(product: SearchSuggestion): void {
    this.typesenseService.clearSuggestions();
    this.searchBarContent.set('');
    this.showDropdown.set(false);

    if (product.productUrl) {
      this.router.navigateByUrl(`/shop/${product.productUrl}`);
    }
    this.closeMobileOverlay();
  }

  /**
   * Resolves the product cut-off for the cards that could be closed (DEV-11470).
   *
   * These cards are painted straight from the Typesense document, which can only carry the
   * RULE — the index is shared by every customer and being closed depends on whose delivery it
   * is. So the rule is the filter: ask the catalog only about the suggestions whose rule covers
   * this customer's delivery day. On the PF catalog that is 1 product in 1391, so in practice
   * this costs nothing on almost every search, and never more than one batched request.
   *
   * Nothing happens without a delivery date, which is every anonymous visitor: no rule can
   * apply to a delivery that does not exist, and the backend would answer null anyway.
   */
  #resolveCutOff(target: WritableSignal<SearchSuggestion[]>): void {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    const deliveryDate = session?.deliveryInfo?.deliveryDate;
    if (!deliveryDate) return;

    const candidates = target().filter(s => hasCutOffRuleForDeliveryDay(s.cutOffDeliveryDays, deliveryDate));
    if (!candidates.length) return;

    // Expanded variant cards share one product: `id` is "38400_attr_36983" on those, and
    // `parentProductId` the only id the catalog knows.
    const productIds = [...new Set(
      candidates.map(s => parseInt(s.parentProductId || s.id, 10)).filter(id => !isNaN(id))
    )];
    if (!productIds.length) return;

    this.productService.getProductsDetailsByIds(productIds)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((products: any) => {
        // Two shapes come back from this endpoint depending on the caller; both are read the
        // same way elsewhere in this component.
        const resolved = new Map<number, any>();
        for (const product of Object.values(products ?? {}) as any[])
          if (product?.id) resolved.set(Number(product.id), product);
        if (!resolved.size) return;

        target.update(suggestions => suggestions.map(suggestion => {
          const product = resolved.get(parseInt(suggestion.parentProductId || suggestion.id, 10));
          if (!product) return suggestion;
          return {
            ...suggestion,
            isCutOffClosed: isProductCutOffLocked(product),
            cutOffNotice: getProductCutOffNotice(product),
          };
        }));
      });
  }

  async onProductAddToCart(product: SearchSuggestion): Promise<void> {
    // Route to PDP when:
    //  - user is anonymous (quick-add requires a session)
    //  - product needs variant selection (canQuickAdd=false on the indexed doc)
    //  - product is OOS by the indexed stockInfo (let the PDP surface alternatives / notify)
    //  - product has closed for this delivery (DEV-11470): the order would refuse the add
    if (!this.hasSession() || !product.canQuickAdd || product.stock?.isOut || product.isCutOffClosed) {
      if (product.productUrl) {
        this.router.navigateByUrl(`/shop/${product.productUrl}`);
        this.showDropdown.set(false);
        this.searchBarContent.set('');
        this.typesenseService.clearSuggestions();
        this.closeMobileOverlay();
      }
      return;
    }

    // Quick-add for simple one-time products
    // Validation checks
    if (isAbandonedUser(this.localStorageService, this.modalContentService, this.activeModal)) return;
    if (isAddressUpdateRequired(this.localStorageService, this.modalContentService, this.activeModal, this.router)) return;
    if (isNoAddressUser(this.localStorageService, this.modalContentService, this.activeModal, this.router)) return;
    if (isANoPaymentMethodUser(this.localStorageService, this.modalContentService, this.activeModal)) return;

    // If the user has their order marked as skipped, prompt to unskip
    const hasSkipped = await this.productCardV2Service.hasSkippedOrder();
    if (hasSkipped) return;

    // Fetch full product details and add to cart
    const productId = parseInt(product.parentProductId || product.id, 10);
    this.addingToCartProductId.set(product.id);

    this.productService.getProductsDetailsByIds([productId])
      .pipe(
        take(1),
        tap((products: any) => {
          const fullProduct = Array.isArray(products) ? products[0] : products[productId];
          if (!fullProduct) {
            this.notificationService.show({ text: 'Could not load product details.', type: 'error' });
            return;
          }

          // Closed between the suggestion being painted and this click — or never resolved,
          // because the rule check runs off the index and this payload is the authority.
          if (isProductCutOffLocked(fullProduct)) {
            this.notificationService.show({ text: PRODUCT_CUTOFF_LOCK_LABEL, type: 'info', delay: 3500 });
            this.addingToCartProductId.set(null);
            return;
          }

          // Build stock map from product's stockInfo and get variant
          const stockMap = arrayToMapMultiKey(['productId', 'attribute.id', 'attribute.value.id'], fullProduct.stockInfo || []);
          const selectedAttribute = product.attributeId && product.attributeValueId
            ? { id: product.attributeId, value: { id: product.attributeValueId } }
            : null;
          const variant = this.productCardV2Service.getVariant(stockMap, fullProduct.id, selectedAttribute);

          if (!variant) {
            // OOS at click time — surface alternatives on the PDP rather than leaving the user
            // staring at a toast. The dropdown has no stock data upfront, so this is the earliest
            // point we know.
            this.notificationService.show({
              text: `${fullProduct.name.trim()} is sold out — choose an alternative on the product page.`,
              type: 'info',
              delay: 3500
            });
            this.addingToCartProductId.set(null);
            if (product.productUrl) {
              this.showDropdown.set(false);
              this.router.navigateByUrl(`/shop/${product.productUrl}`);
              this.closeMobileOverlay();
            }
            return;
          }

          const data: UpdateOrderParams = {
            product: {
              ...fullProduct,
              variantId: variant.id,
              quantity: 1,
              // Fall back to the product's own packages: a packaged product must never
              // ship packageId null (ELIO-6622), even if the indexed suggestion lacked it
              packageId: product.packageId || (fullProduct.packages?.length ? +fullProduct.packages[0].id : null),
            },
            type: ProductType.common,
            orderId: this.orderService.order()?.id || null
          };

          this.orderService.updateOrder(data, false, false)
            .pipe(take(1))
            .subscribe((res: any) => {
              if (res.data?.order?.outOfStockProducts?.length || res.data?.outOfStockProducts?.length || res.data?.order?.notAvailableProducts?.length || res.data?.notAvailableProducts?.length) return;
              const variantLabel = product.variantLabel ? ` (${product.variantLabel})` : '';
              this.notificationService.show({
                text: `${fullProduct.name.trim()}${variantLabel} added to order!`,
                type: 'success',
                delay: 3000
              });

              updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.firebaseCrudService);
              this.klaviyoService.trackEvent(KLAVIYOEVENTS.ProductAddedToCart, this.orderService.setupProductForKlaviyo(fullProduct));
              trackAddToCartEvent(this.dataLayerService, { ...fullProduct, quantity: 1 });
            });

          this.showDropdown.set(false);
          this.searchBarContent.set('');
          this.typesenseService.clearSuggestions();
          this.addingToCartProductId.set(null);
          this.closeMobileOverlay();
        })
      ).subscribe({
        error: () => this.addingToCartProductId.set(null)
      });
  }

  // Computed to determine which products to display
  displayProducts = computed(() => {
    const query = this.searchBarContent();
    if (query && query.length >= 2) {
      return this.suggestions();
    }
    return this.popularProducts();
  });

  // Header text for products section
  productsHeaderText = computed(() => {
    const query = this.searchBarContent();
    if (query && query.length >= 2) {
      return 'Products';
    }
    return 'Popular Products';
  });

  private setUpFilterTypesMap() {
    for (const type of Object.values(FilterTypes)) {
      const key: any = type;
      this.filterTypes.set(key, signal(0));
    }
  }

  clearSearchInput(): void {
    this.searchBarContent.set('');
    this.typesenseService.clearSuggestions();
    // Reset the debounced pipeline so retyping a previously-searched term refetches.
    this.searchInput$.next('');
    this.searchInput.nativeElement.focus();
  }

  onSearchSubmit(): void {
    const query = this.searchBarContent();
    if (query && query.length >= 2) {
      this.navigateToSearch(query);
    }
  }

  inputSearchTypeHandler(event: any) {
    const value = event.target.value;
    // Reopen dropdown if closed (e.g. after a search navigation while input kept focus)
    if (!this.showDropdown()) this.showDropdown.set(true);
    // Emit to Typesense autocomplete
    this.searchInput$.next(value);
    // Reset selected index when typing — -1 means "no row pre-selected"
    this.selectedIndex = -1;
  }

  onFilterTypeSelected(filterType: FilterTypes) {
    const filterExists = this.filters().get(
      `${filterType}${this.searchBarContent()}`
    );
    if (filterExists) return;

    const current = this.filterTypes.get(filterType);
    if (current) {
      current.update((value: number) => value + 1);
      this.add(filterType);
    }
  }

  add(filterType: FilterTypes): void {
    const value = (this.searchBarContent() || '').trim();
    if (value) {
      this.filters().set(`${filterType}${value}`, { type: filterType, value });
      this.signalsStoreService.setFilters(this.filters());
      this.searchBarContent.set('');
      this.searchInput.nativeElement.focus();
      this.search();
    }
  }

  remove(key: string): void {
    if (key && this.filters().has(key)) {
      const type = this.filters().get(key)?.type;
      if (!type) return;

      this.filters().delete(key);
      this.signalsStoreService.setFilters(this.filters());

      const current = this.filterTypes.get(type);
      if (current) current.update((value: number) => value - 1);

      this.searchInput.nativeElement.focus();

      if (this.filters().size > 0) this.search();
      else this.headerService.clearFilters(false);
    }
  }

  search() {
    const filterParams: FilterParams[] = this.setUpFilterParams();

    // If the current loaded component is not _ShopComponent, should redirect the client to it:
    this.headerService.setFiltering(true);
    if (!this.isInShopComponent()) {
      this.router.navigateByUrl('/shop');
    }
    this.productService.getProductsByFilter(filterParams);
  }

  private setUpFilterParams(): FilterParams[] {
    const params: Map<FilterType, string[]> = new Map();
    this.filters().forEach(
      (value: { type: FilterTypes; value: string }, key: string) => {
        if (!params.has(value.type)) {
          params.set(value.type, [value.value]);
        } else {
          const current = params.get(value.type);
          if (current) {
            current.push(value.value);
            params.set(value.type, current);
          }
        }
      }
    );

    return Array.from(params.entries()).map(([type, values]) => ({
      type,
      values,
    }));
  }

  toggleSearchBar() {
    this.headerService.clearFilters();
  }

  /**
   * Mobile overlay Cancel: clear the input + suggestions, then delegate to
   * headerService.clearFilters which already exits search mode, resets filtering
   * state, and on /shop reloads the base category (which also strips ?q=).
   */
  cancelMobile(): void {
    this.searchBarContent.set('');
    this.typesenseService.clearSuggestions();
    // Reset distinctUntilChanged memory so retyping the previous query later refetches.
    this.searchInput$.next('');
    this.headerService.clearFilters(true);
    // Intentionally NOT calling restorePreSearchScroll: if the user opened the overlay but
    // never ran a query, scroll never moved, so "restoring" would actually push the viewport
    // to the captured top-product (a perceived jump). preSearchScroll is overwritten on the
    // next bar-open anyway, so leaving it set is harmless.
  }
}
